import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertCircle,
  BarChart3,
  Calendar,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Filter,
  FolderKanban,
  Gauge,
  Home,
  LayoutDashboard,
  Layers,
  LineChart,
  LogOut,
  Map as MapIcon,
  Menu,
  Moon,
  Sun,
  Settings2,
  Search,
  Settings,
  Train,
  type LucideIcon,
} from "lucide-react";
import clsx from "clsx";

import { api } from "@/api/client";
import { useTheme } from "@/lib/theme";
import { useUi, type RangeKey } from "@/lib/store";
import { aggregationBadge, resolveAggregation } from "@/lib/aggregation";
import { AggregationDialog } from "@/components/AggregationDialog";
import { TimeRangeDialog } from "@/components/TimeRangeDialog";
import { DeviceFilterDialog } from "@/components/DeviceFilterDialog";
import { SEVERITY, STATUS } from "@/lib/palette";
import { ClientLogo } from "@/components/ClientLogo";

const RANGES: RangeKey[] = ["ALL", "1Y", "1M", "1W", "1D"];

export function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectCode, setProject, range, setRangeKey, aggregation, sidebarOpen, toggleSidebar } =
    useUi();
  const { theme, toggle: toggleTheme } = useTheme();
  const [aggregationOpen, setAggregationOpen] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(true);
  const deviceFilters = useUi((s) => s.deviceFilters);
  const aggregationText = aggregationBadge(resolveAggregation(aggregation, range));
  const customRange = range.key === "CUSTOM" || range.key === "RELATIVE";

  // The portfolio home spans every project, so the per-project chart controls
  // (device filter, aggregation, time range) mean nothing there.
  const onHome = location.pathname === "/";
  const onAdmin = location.pathname.startsWith("/admin") || location.pathname.startsWith("/system");

  const { data: projects } = useQuery({
    queryKey: ["projects"],
    queryFn: api.projects,
  });
  const sortedProjects = [...(projects ?? [])].sort((a, b) => a.name.localeCompare(b.name));

  const { data: me } = useQuery({ queryKey: ["me"], queryFn: api.me });

  const project = projects?.find((p) => p.code === projectCode) ?? projects?.[0];

  const { data: display } = useQuery({
    queryKey: ["display", project?.code],
    queryFn: () => api.display(project!.code),
    enabled: Boolean(project),
  });

  const { data: alarmSummary } = useQuery({
    queryKey: ["alarm-summary", project?.code],
    queryFn: () => api.alarmSummary(project!.code),
    enabled: Boolean(project),
    // Alarms are the one thing that should not wait for a page change.
    refetchInterval: 60_000,
  });

  const features = display?.features;

  const projectNav: { to: string; label: string; icon: LucideIcon; show?: boolean }[] = [
    { to: "/dashboards", label: "Dashboards", icon: LayoutDashboard, show: true },
    { to: "/crossings", label: "Train crossings", icon: LineChart, show: true },
    { to: "/overview", label: "Overview", icon: Activity, show: true },
    { to: "/events", label: "Events", icon: Train, show: features?.events },
    { to: "/data", label: "Data & Statistics", icon: BarChart3, show: features?.statistics },
    { to: "/fatigue", label: "Fatigue", icon: Gauge, show: features?.fatigue },
    { to: "/map", label: "Site map", icon: MapIcon, show: features?.map },
    { to: "/alarms", label: "Alarms", icon: AlertCircle, show: features?.alarms },
  ];

  /** Selecting a project always lands on its dashboard. */
  function openProject(code: string) {
    setProject(code);
    navigate("/dashboards");
  }

  function signOut() {
    void api.logout();   // clears locally at once; revokes on the server too
    navigate("/login", { replace: true });
  }

  const sideLink = (to: string, label: string, Icon: LucideIcon, compact = false) => (
    <NavLink
      key={to}
      to={to}
      end={to === "/"}
      className={({ isActive }) =>
        clsx(
          "flex items-center gap-2.5 rounded-lg px-2.5 text-sm transition-colors",
          compact ? "py-1.5" : "py-2",
          isActive
            ? "bg-brand-600/15 text-brand-300"
            : "text-ink-300 hover:bg-ink-800 hover:text-ink-100",
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{label}</span>
      {to === "/alarms" && (alarmSummary?.unacknowledged ?? 0) > 0 && (
        <span
          className="ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-ink-950"
          style={{ backgroundColor: SEVERITY[1] }}
        >
          {alarmSummary!.unacknowledged}
        </span>
      )}
    </NavLink>
  );

  return (
    <div className="flex h-full">
      {/* ---- Sidebar ---- */}
      <aside
        className={clsx(
          // Width only: this animates the sidebar collapsing. transition-all
          // also faded its colour, so on a theme switch the sidebar lagged a
          // fifth of a second behind a page that had already changed.
          "flex shrink-0 flex-col border-r border-ink-800 bg-ink-900 transition-[width] duration-200",
          sidebarOpen ? "w-64" : "w-0 overflow-hidden",
        )}
      >
        {/* The client's mark leads; the platform is named underneath, small,
            as the thing it runs on. */}
        <div className="px-5 pb-4 pt-5">
          <ClientLogo height={44} />
          <div className="mt-2.5 flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-ink-400">
            <Activity className="h-3 w-3 text-brand-400" strokeWidth={2.5} />
            Kinetix SHM Platform
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
          <div className="label px-2 pb-1.5">Workspace</div>
          <nav className="space-y-0.5">{sideLink("/", "Home", Home)}</nav>

          <button
            type="button"
            onClick={() => setProjectsOpen((o) => !o)}
            className="mt-2 flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-ink-300 hover:bg-ink-800 hover:text-ink-100"
            aria-expanded={projectsOpen}
          >
            <FolderKanban className="h-4 w-4 shrink-0" />
            <span>Projects</span>
            <span className="ml-auto text-[11px] text-ink-500">{projects?.length ?? 0}</span>
            {projectsOpen ? (
              <ChevronDown className="h-3.5 w-3.5 text-ink-500" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-ink-500" />
            )}
          </button>

          {projectsOpen && (
            <ul className="mt-0.5 space-y-0.5">
              {sortedProjects.map((p) => {
                const current = p.code === project?.code;
                const highlighted = current && !onHome && !onAdmin;
                const online = (p.status?.online_devices ?? 0) > 0;
                return (
                  <li key={p.code}>
                    <button
                      type="button"
                      onClick={() => openProject(p.code)}
                      className={clsx(
                        "w-full rounded-lg px-2.5 py-1.5 text-left transition-colors",
                        highlighted ? "bg-ink-800" : "hover:bg-ink-800",
                      )}
                      title={`Open ${p.name}`}
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className="h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{ backgroundColor: online ? STATUS.good : STATUS.critical }}
                          aria-label={online ? "online" : "offline"}
                        />
                        <span
                          className={clsx(
                            "truncate text-[13px] font-medium",
                            highlighted ? "text-brand-300" : "text-ink-200",
                          )}
                        >
                          {p.name}
                        </span>
                      </span>
                      <span className="block truncate pl-3.5 text-[11px] text-ink-500">
                        {p.project_number ?? p.code}
                      </span>
                    </button>

                    {/* The selected project's pages, beneath it. */}
                    {highlighted && (
                      <nav className="mb-1 ml-4 mt-0.5 space-y-0.5 border-l border-ink-800 pl-2">
                        {projectNav
                          .filter((item) => item.show)
                          .map((item) => sideLink(item.to, item.label, item.icon, true))}
                      </nav>
                    )}
                  </li>
                );
              })}
              {!sortedProjects.length && (
                <li className="px-2.5 py-1.5 text-xs text-ink-500">No projects yet</li>
              )}
            </ul>
          )}

          {/* Always available: without it there is no way to create the first project. */}
          <nav className="mt-3 space-y-0.5 border-t border-ink-800 pt-3">
            {sideLink("/admin", "Administration", Settings2)}
          </nav>
        </div>

        <div className="border-t border-ink-800 p-3">
          <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5">
            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-ink-700 text-xs font-semibold">
              {(me?.full_name ?? me?.email ?? "?").slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1 leading-tight">
              <div className="truncate text-[13px] font-medium">
                {me?.full_name ?? me?.email ?? "—"}
              </div>
              <div className="truncate text-[11px] text-ink-400">
                {me?.email && me.email !== me.full_name ? me.email : ""}
              </div>
            </div>
            <button
              onClick={signOut}
              title="Sign out"
              className="rounded p-1.5 text-ink-400 hover:bg-ink-800 hover:text-ink-200"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* ---- Main ---- */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex shrink-0 items-center gap-3 border-b border-ink-800 bg-ink-900/60 px-4 py-2.5 backdrop-blur">
          <button
            onClick={toggleSidebar}
            className="rounded-lg p-2 text-ink-300 hover:bg-ink-800"
            title="Toggle sidebar"
          >
            <Menu className="h-4 w-4" />
          </button>

          {/* Project selector. On the home page it is a way in; elsewhere it
              switches the project the current page shows. */}
          <div className="relative min-w-0 flex-1 max-w-xl">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-500" />
            <select
              value={onHome ? "" : project?.code ?? ""}
              onChange={(e) => {
                if (!e.target.value) return;
                if (onHome) openProject(e.target.value);
                else setProject(e.target.value);
              }}
              className="w-full appearance-none rounded-lg border border-ink-700 bg-ink-850 py-2 pl-9 pr-9 text-sm text-ink-100 focus:border-brand-500 focus:outline-none"
              aria-label="Project"
            >
              {onHome && <option value="">Open a project…</option>}
              {sortedProjects.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.name}
                  {p.project_number ? ` · ${p.project_number}` : ""}
                </option>
              ))}
            </select>
            <ChevronsUpDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-500" />
          </div>

          {!onHome && (
            <>
              {/* Filters: which devices every dashboard chart shows. */}
              {(() => {
                const active = project ? deviceFilters[project.code]?.length ?? 0 : 0;
                return (
                  <button
                    type="button"
                    onClick={() => setFilterOpen(true)}
                    disabled={!project}
                    className={clsx(
                      "flex items-center gap-2 rounded-lg border px-2.5 py-1 text-left hover:bg-ink-800 disabled:opacity-50",
                      active ? "border-brand-500/60 bg-brand-600/10" : "border-ink-700 bg-ink-850",
                    )}
                    title="Filter devices"
                  >
                    <Filter className={clsx("h-4 w-4", active ? "text-brand-300" : "text-ink-400")} />
                    <span className="leading-tight">
                      <span className="block text-[9px] uppercase tracking-wider text-ink-500">
                        Filters
                      </span>
                      <span className="block whitespace-nowrap text-xs font-semibold text-ink-100">
                        {active ? `${active} DEVICE${active === 1 ? "" : "S"}` : "NONE"}
                      </span>
                    </span>
                  </button>
                );
              })()}

              {/* Aggregation: how every chart reduces its data. */}
              <button
                type="button"
                onClick={() => setAggregationOpen(true)}
                className="flex items-center gap-2 rounded-lg border border-ink-700 bg-ink-850 px-2.5 py-1 text-left hover:bg-ink-800"
                title="Aggregation"
              >
                <Layers className="h-4 w-4 text-ink-400" />
                <span className="leading-tight">
                  <span className="block text-[9px] uppercase tracking-wider text-ink-500">
                    Aggregation
                  </span>
                  <span className="block whitespace-nowrap text-xs font-semibold text-ink-100">
                    {aggregationText}
                  </span>
                </span>
              </button>

              {/* Time range: one row of controls above the charts, per the viz rules. */}
              <div className="flex items-center gap-1 rounded-lg border border-ink-700 bg-ink-850 p-0.5">
                {RANGES.map((key) => (
                  <button
                    key={key}
                    onClick={() => setRangeKey(key)}
                    className={clsx(
                      "rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors",
                      range.key === key
                        ? "bg-brand-600 text-white"
                        : "text-ink-300 hover:bg-ink-800",
                    )}
                  >
                    {key}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setRangeOpen(true)}
                  className={clsx(
                    "grid h-7 w-7 place-items-center rounded-md transition-colors",
                    customRange ? "bg-brand-600 text-white" : "text-ink-300 hover:bg-ink-800",
                  )}
                  title={
                    customRange
                      ? `${range.start.toLocaleString()} – ${range.end.toLocaleString()}`
                      : "Custom time range"
                  }
                  aria-label="Custom time range"
                >
                  <Calendar className="h-3.5 w-3.5" />
                </button>
              </div>
            </>
          )}

          <button
            type="button"
            onClick={toggleTheme}
            className="ml-auto rounded-lg p-2 text-ink-300 hover:bg-ink-800"
            title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
          >
            {/* The icon shows what a click gives you, not what you have. */}
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          <NavLink
            to="/system"
            className="rounded-lg p-2 text-ink-300 hover:bg-ink-800"
            title="System"
          >
            <Settings className="h-4 w-4" />
          </NavLink>
        </header>

        <AggregationDialog open={aggregationOpen} onClose={() => setAggregationOpen(false)} />
        <TimeRangeDialog open={rangeOpen} onClose={() => setRangeOpen(false)} />
        {project && (
          <DeviceFilterDialog
            open={filterOpen}
            onClose={() => setFilterOpen(false)}
            projectCode={project.code}
          />
        )}

        {/*
          Keyed on the theme so a switch redraws every chart. Everything else
          on the page follows the theme through CSS the instant the attribute
          changes, but a chart's colours were baked into its option when it was
          built, and a chart only rebuilds its option when its data changes -
          so without this, switching theme would recolour the page and leave
          every chart in the old one until the next refresh. Remounting is
          cheap because the data comes back from the query cache, which lives
          above this and survives it; only local view state, like a chart's
          zoom, is reset.
        */}
        <main key={theme} className="min-h-0 flex-1 overflow-auto bg-ink-950">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
